import { regionData, codeToText } from "element-china-area-data";

export type ChinaRegionValue = {
  province: string;
  city: string;
  district: string;
  regionCode: string;
};

export type ChinaRegionOption = {
  label: string;
  value: string;
  children?: ChinaRegionOption[];
};

export const CHINA_REGION_OPTIONS: ChinaRegionOption[] = regionData.map((p) => ({
  label: p.label,
  value: p.value,
  children: p.children?.map((c) => ({
    label: c.label,
    value: c.value,
    children: c.children?.map((d) => ({ label: d.label, value: d.value })),
  })),
}));

export function composeChinaAddress(
  province: string,
  city: string,
  district: string,
  detail?: string,
): string {
  const region = [province, city, district].map((s) => s.trim()).filter(Boolean).join("");
  const d = (detail ?? "").trim();
  if (!region) return d;
  if (!d) return region;
  return region + d;
}

export function emptyChinaRegion(): ChinaRegionValue {
  return { province: "", city: "", district: "", regionCode: "" };
}

export function regionFromCode(code: string): ChinaRegionValue {
  const c = (code ?? "").trim();
  if (!c) return emptyChinaRegion();
  const texts = codeToText[c];
  if (!texts) return emptyChinaRegion();
  return {
    province: texts[0] ?? "",
    city: texts[1] ?? "",
    district: texts[2] ?? "",
    regionCode: c,
  };
}

export function findProvinceOption(provinceName: string): ChinaRegionOption | undefined {
  const name = provinceName.trim();
  if (!name) return undefined;
  return CHINA_REGION_OPTIONS.find((p) => p.label === name);
}

export function findCityOption(province: ChinaRegionOption | undefined, cityName: string): ChinaRegionOption | undefined {
  const name = cityName.trim();
  if (!province || !name) return undefined;
  return province.children?.find((c) => c.label === name);
}

export function findDistrictCode(
  province: ChinaRegionOption | undefined,
  city: ChinaRegionOption | undefined,
  districtName: string,
): string {
  const name = districtName.trim();
  if (!city || !name) return "";
  const hit = city.children?.find((d) => d.label === name);
  return hit?.value ?? "";
}

export function regionFromNames(
  provinceName: string,
  cityName: string,
  districtName: string,
): ChinaRegionValue {
  const provinceOpt = findProvinceOption(provinceName);
  const cityOpt = findCityOption(provinceOpt, cityName);
  const districtCode = findDistrictCode(provinceOpt, cityOpt, districtName);
  return {
    province: provinceName.trim(),
    city: cityName.trim(),
    district: districtName.trim(),
    regionCode: districtCode,
  };
}
